import { useRef, useState, useEffect, useMemo } from "react";
import { Button, HStack, Popover, Portal, Separator, SimpleGrid, Text, VStack, RadioGroup, Box, CloseButton, Heading, Float, Circle } from "@chakra-ui/react";

import {
  LuArrowRightLeft,
  LuCircleSlash,
  LuCopy,
  LuEllipsis,
  LuGitMerge,
  LuLassoSelect,
  LuMerge,
  LuMove,
  LuRotateCcw,
  LuScissorsLineDashed,
  LuSendToBack,
  LuSeparatorHorizontal,
  LuSquare,
  LuSquareFunction,
  LuSquarePen,
  LuSquareSplitHorizontal,
  LuTrash,
  LuUndo,
  LuUndo2,
  LuX,
} from "react-icons/lu";
import {
  matchKeyInText,
  saveToCache,
  getFromCache,
  showToast,
  flyToBBox,
  removeEntities,
  reverseWaypointsEntities,
  duplicateEntities,
  enableGroupDrag,
  getCombinedKeysText,
} from "../../../utilies/Utility";
import { useAppStore } from "../../../context/store";
import Type from "../../../utilies/Types";
import { Cartesian3, Cartographic, Color, ConstantPositionProperty, ConstantProperty, EllipsoidGeodesic, PolygonHierarchy } from "cesium";

import {
  applyPPSelectionToCoordinate,
  deleteDrawingForSelect,
  enableRectangleDrawing,
  deleteSelectionFromViewer,
  highlightEntitiesInBounds,
  resetHIBHighlights,
  registerIsInBoundingClick,
  restoreEntityColors,
  enableRectSelectAndCutByCutline,
  enableRectangleDrawingWithControlKey,
} from "../../../utilies/GlobalToolsUtility";
import { Tooltip } from "../../ui/tooltip";
import { highlightPolylineEndpointsAcrossDataSources } from "../../../utilies/globalTools/IntersectionPoints";
import { handleCuttingGeojsonLinesGlobal, undoLastCut } from "../../../utilies/globalTools/Split";
import { mergeEntities } from "../../../utilies/globalTools/Merge";
import { removeSelectedEntity, toggleAutoSelect } from "../../../utilies/selection/Auto";
import { restoreCameraControls, computeZoomLevel } from "../../../utilies/MapUtility";

import { rearrangePoints } from "../../../utilies/EntityUtility";
import { highlightEntityForSelection } from "../../../utilies/other/GlobalMultipleSelection";
import { useMapObjects } from "../../../context/MapObjectsContext";
import { colorPolylinesBasedOnSharedEndpoints } from "../../../utilies/globalTools/DanglingLines";
import { toggleAlignEndpointsTool } from "../../../utilies/globalTools/AlignEndpoints";
import { findLaneletEntitiesForId, getPropertyValue, switchLeftRightLines } from "../../../utilies/lanelet/CheckerByIds";
import { setPropertyValue } from "../../../utilies/entity/Property";
function GlobalTools() {
  const savedHotkeysRef = useAppStore((s) => s.savedHotkeysRef);
  const viewerRef = useAppStore((s) => s.viewerRef);
  const globalMode = useAppStore((s) => s.globalMode);
  const intersectionPointsEnabled = useAppStore((s) => s.intersectionPointsEnabled);
  const setIntersectionPointsEnabled = useAppStore.getState().setIntersectionPointsEnabled;
  const userData = useAppStore((s) => s.userData);

  const [isDisableSwitchLaneletSides, setIsDisableSwitchLaneletSides] = useState(true);
  const [isEnableSwitchLaneletTurnDirection, setIsEnableSwitchLaneletTurnDirection] = useState(false);

  const [intersectionPointsUseAll, setIntersectionPointsUseAll] = useState(false);
  const intersectionPointsUseAllRef = useRef(false);
  useEffect(() => {
    intersectionPointsUseAllRef.current = intersectionPointsUseAll;
  }, [intersectionPointsUseAll]);

  const originalCameraControls = useAppStore((s) => s.originalCameraControls);
  const { mapObjects, setMapObjects, mapObjectsRef } = useMapObjects();

  const intersectionDataSourcesAll = useMemo(() => {
    const temp = [];
    for (const key in mapObjects) {
      if (
        matchKeyInText("curb_stone", mapObjects[key].file_name) ||
        matchKeyInText("road_edge", mapObjects[key].file_name) ||
        matchKeyInText("lane_marker", mapObjects[key].file_name) ||
        matchKeyInText("cut_line", mapObjects[key].file_name) ||
        matchKeyInText("stop_line", mapObjects[key].file_name) ||
        matchKeyInText("yield_line", mapObjects[key].file_name) ||
        matchKeyInText("virtual_line", mapObjects[key].file_name) ||
        matchKeyInText("turning_line", mapObjects[key].file_name) ||
        matchKeyInText("lanelet", mapObjects[key].file_name)
      ) {
        temp.push(mapObjects[key].dataSource);
      }
    }
    return temp;
  }, [mapObjects]);

  const intersectionDataSourcesLimited = useMemo(() => {
    const temp = [];
    for (const key in mapObjects) {
      const fn = mapObjects[key].file_name;

      // Only: Virtual line / Lane Marker / Turning Line / Turning Arc Lanelet
      if (
        matchKeyInText("virtual_line", fn) ||
        matchKeyInText("lane_marker", fn) ||
        matchKeyInText("turning_line", fn) ||
        matchKeyInText("turning_arc_lanelet", fn) ||
        (matchKeyInText("lanelet", fn) && matchKeyInText("turning_arc", fn))
      ) {
        temp.push(mapObjects[key].dataSource);
      }
    }
    return temp;
  }, [mapObjects]);

  const alignDataSources = useMemo(() => {
    const keys = ["lane_marker", "virtual_line", "curbstone", "road_edge", "turning_line", "lanelet"];
    const temp = [];
    for (const key in mapObjects) {
      const fn = String(mapObjects[key]?.file_name || "").toLowerCase();
      if (keys.some((k) => fn.includes(k))) temp.push(mapObjects[key].dataSource);
    }
    return temp;
  }, [mapObjects]);

  const alignDataSourcesRef = useRef([]);
  useEffect(() => {
    alignDataSourcesRef.current = alignDataSources;
  }, [alignDataSources]);

  const intersectionDataSourcesAllRef = useRef([]);
  useEffect(() => {
    intersectionDataSourcesAllRef.current = intersectionDataSourcesAll;
  }, [intersectionDataSourcesAll]);

  const intersectionDataSourcesLimitedRef = useRef([]);
  useEffect(() => {
    intersectionDataSourcesLimitedRef.current = intersectionDataSourcesLimited;
  }, [intersectionDataSourcesLimited]);

  // Track keys so Shift+W+A behaves like Shift+W but "A" switches the datasource set while enabled
  const getIntersectionDataSourcesNow = () => {
    const useAll = !!intersectionPointsUseAllRef.current;
    return useAll ? intersectionDataSourcesAllRef.current : intersectionDataSourcesLimitedRef.current;
  };

  const intersectionAutoRef = useRef({
    tid: null,
    lastCenter: null,
  });

  const cutHistory = useAppStore((s) => s.cutHistory);
  const directionArrowSettingRef = useAppStore((s) => s.directionArrowSettingRef);
  const globalToolsOpen = useAppStore((s) => s.globalToolsOpen);
  const userType = useAppStore((s) => s.userType);
  const userCanEditing = useAppStore((s) => s.userCanEditing);
  const selectedEntitiesRef = useAppStore((s) => s.selectedEntitiesRef);
  const selectedEntities = useAppStore((s) => s.selectedEntities);
  const simplifyToleranceSettingRef = useAppStore((s) => s.simplifyToleranceSettingRef);
  const globalUndoStackRef = useAppStore((s) => s.globalUndoStackRef);

  const canUseAlign = useMemo(() => {
    return userCanEditing && Array.isArray(alignDataSources) && alignDataSources.length > 0;
  }, [userCanEditing, alignDataSources]);
  // setters
  const setCanOpenInfobox = useAppStore.getState().setCanOpenInfobox;
  const setGlobalMode = useAppStore.getState().setGlobalMode;

  const setIsOpenEditCoordianteModal = useAppStore.getState().setIsOpenEditCoordianteModal;
  const setEditCoordianteModalData = useAppStore.getState().setEditCoordianteModalData;

  const setGlobalToolsOpen = useAppStore.getState().setGlobalToolsOpen;
  const setCanHightlightEntity = useAppStore.getState().setCanHightlightEntity;
  const setSelectedEntities = useAppStore.getState().setSelectedEntities;
  const setBultEditDialog = useAppStore.getState().setBultEditDialog;
  const setSelectedFeatures = useAppStore.getState().setSelectedFeatures;
  const pushUndoAction = useAppStore.getState().pushUndoAction;
  const popUndoAction = useAppStore.getState().popUndoAction;

  const splitRef = useRef(null);
  const splitSingleUseRef = useRef(false);
  const selectRef = useRef(null);

  const [isSticky, setIsSticky] = useState(false);

  const selectionTypeOptions = [
    { label: "Features: Full Cover", value: "f_f" },
    { label: "Points: Any Location", value: "p_f" },
    { label: "Features: Partial Cover", value: "f_p" },
    { label: "Points: Only Start or End", value: "p_p" },
  ];
  const [selectionType, setSelectionType] = useState("f_f");

  const selectionTypeFunction = (newVlaue) => {
    setSelectionType(newVlaue);
    saveToCache(Type.STORAGE.SELECTION_TYPE, newVlaue);
  };
  useEffect(() => {
    const temp = getFromCache(Type.STORAGE.SELECTION_TYPE);
    if (temp) {
      setSelectionType(temp);
    }
  }, []);

  const [drawnBBOX, setDrawnBBOX] = useState(null);
  const [lastSelectedWithRectangle, setLastSelectedWithRectangle] = useState(null);
  const [multiBoxClusterSelections, setMultiBoxClusterSelections] = useState([]);
  const multiBoxClusterModeRef = useRef(false);
  const lastCtrlTapAtRef = useRef(0);

  const normalizeLayerNameForSnap = (value = "") =>
    String(value)
      .toLowerCase()
      .replace(/[\s_\-]/g, "");

  const getEntityAndDataSourceById = (id) => {
    const viewer = viewerRef.current;
    if (!viewer || !id) return { entity: null, dataSource: null };

    const dsList = viewer.dataSources?._dataSources || [];
    for (const ds of dsList) {
      const coll = ds?.entities;
      if (!coll) continue;

      const found = coll.getById ? coll.getById(id) : null;
      if (found) return { entity: found, dataSource: ds };

      const vals = coll.values || [];
      for (const ent of vals) {
        if (ent?.id === id) return { entity: ent, dataSource: ds };
      }
    }

    const main = viewer.entities;
    if (main) {
      const found = main.getById ? main.getById(id) : null;
      if (found) return { entity: found, dataSource: null };

      const vals = main.values || [];
      for (const ent of vals) {
        if (ent?.id === id) return { entity: ent, dataSource: null };
      }
    }

    return { entity: null, dataSource: null };
  };

  const getSnapLayerGroup = (item) => {
    const id = item?.id ?? item?.entity?.id ?? item?.entityId ?? null;
    const { entity, dataSource } = getEntityAndDataSourceById(id);

    const entityCollection = entity?.entityCollection || entity?._entityCollection || null;
    const owner = dataSource || entityCollection?.owner || entityCollection?._owner || null;

    const rawLayerName = owner?.layerName || owner?.name || owner?._name || item?.dataSourceName || "";

    const normalized = normalizeLayerNameForSnap(rawLayerName);

    if (normalized.includes("lanelet") && !normalized.includes("turningarc")) return "lanelet";
    if (normalized.includes("virtualline") || normalized.includes("lanemarker") || normalized.includes("turningline") || normalized.includes("turningarc")) {
      return "cluster_group";
    }

    return "other";
  };

  const mergePPSelections = (selections = []) => {
    const valid = selections.filter((s) => Array.isArray(s?.results?.p_p) && s.results.p_p.length > 0);
    if (!valid.length) return null;

    const merged = [];
    const seen = new Set();

    for (const sel of valid) {
      for (const item of sel.results.p_p) {
        const id = item?.id ?? item?.entity?.id ?? item?.entityId ?? JSON.stringify(item);
        const endpoints = item?.endpoints || {};
        const key = [
          id,
          endpoints?.start ? `${Number(endpoints.start.lat).toFixed(7)}_${Number(endpoints.start.lon).toFixed(7)}` : "ns",
          endpoints?.end ? `${Number(endpoints.end.lat).toFixed(7)}_${Number(endpoints.end.lon).toFixed(7)}` : "ne",
        ].join("|");

        if (seen.has(key)) continue;
        seen.add(key);
        merged.push(item);
      }
    }

    return {
      ...valid[0],
      results: {
        ...valid[0].results,
        p_p: merged,
      },
    };
  };

  const resetMultiBoxClusterState = () => {
    multiBoxClusterModeRef.current = false;
    setIsMultiBoxClusterMode(false);
    setMultiBoxClusterSelections([]);
  };

  // Split
  useEffect(() => {
    if (globalMode === "split") {
      try {
        splitRef.current?.destroy();
      } catch {}
      showToast("Split mode enabled", Type.NOTIFICATION.INFO);
      splitRef.current = handleCuttingGeojsonLinesGlobal(
        viewerRef.current,
        originalCameraControls,
        ["lanemarker", "virtualline", "curbstone", "roadedge", "turningline", "guardrail", "concretebarrier", "lanelet"],
        mapObjects,
        setMapObjects,
        cutHistory,
        mapObjectsRef,
        {
          // callback called after a cut attempt; if this was a hotkey activation
          // then we exit split mode after the first real cut.
          onCutDone: (didSplit) => {
            try {
              if (splitSingleUseRef.current && didSplit) {
                setGlobalMode("none");
                splitSingleUseRef.current = false;
              }
            } catch {}
          },
        },

        userData,
      );
    } else {
      try {
        if (splitRef.current) {
          showToast("Split mode disabled", Type.NOTIFICATION.INFO);
        }
        splitRef.current?.destroy();
      } catch {}
      splitRef.current = null;
      splitSingleUseRef.current = false;
    }
  }, [globalMode]);

  // For Select With CTRL
  const rectHandlerRef = useRef(null);

  const [isMultiBoxClusterMode, setIsMultiBoxClusterMode] = useState(false);

  useEffect(() => {
    const onCtrlKeyDown = (e) => {
      if (e.key !== "Control" || e.repeat) return;

      const now = Date.now();
      if (now - lastCtrlTapAtRef.current <= 350) {
        const next = !multiBoxClusterModeRef.current;
        multiBoxClusterModeRef.current = next;
        setIsMultiBoxClusterMode(next);

        if (!next) {
          setMultiBoxClusterSelections([]);
          if (lastSelectedWithRectangle?.multiBoxCluster) {
            setLastSelectedWithRectangle(null);
          }
        }

        showToast(next ? "Multi-box auto snap cluster mode enabled" : "Multi-box auto snap cluster mode disabled", Type.NOTIFICATION.INFO);
      }
      lastCtrlTapAtRef.current = now;
    };

    window.addEventListener("keydown", onCtrlKeyDown);
    return () => window.removeEventListener("keydown", onCtrlKeyDown);
  }, [lastSelectedWithRectangle]);

  useEffect(() => {
    const tryStart = () => {
      if (!viewerRef.current || !originalCameraControls) return;

      clearInterval(timerId);

      rectHandlerRef.current = enableRectangleDrawingWithControlKey(
        viewerRef.current,
        originalCameraControls,
        (bounds) => {
          innerEnableRectangleDrawing(bounds, { source: "ctrl" });
        },
        true,
      );
    };

    const timerId = setInterval(tryStart, 100);
    tryStart();

    return () => {
      clearInterval(timerId);
      if (rectHandlerRef.current) {
        rectHandlerRef.current.destroy?.();
        rectHandlerRef.current = null;
      }
    };
  }, [originalCameraControls, selectionType]);

  // For Select
  useEffect(() => {
    if (!viewerRef.current || !originalCameraControls) return;

    if (globalMode === "select") {
      selectRef.current = enableRectangleDrawing(
        viewerRef.current,
        originalCameraControls,
        (bounds) => {
          innerEnableRectangleDrawing(bounds, { source: "button" });
        },
        true,
      );
    } else {
      try {
        selectRef.current?.destroy();
      } catch {}
      selectRef.current = null;
    }
  }, [globalMode, viewerRef, originalCameraControls, selectionType, setSelectedEntities]);

  const innerEnableRectangleDrawing = (bounds, options = {}) => {
    const { west, south, east, north } = bounds;
    const isCtrlSource = options?.source === "ctrl";
    const useMultiBoxCluster = isCtrlSource && multiBoxClusterModeRef.current && selectionType === "p_p";

    if (!useMultiBoxCluster) {
      resetHIBHighlights(viewerRef.current);
    }

    const res = highlightEntitiesInBounds(viewerRef.current, bounds, selectionType, {
      highlight: selectionType == "f_p" || selectionType == "f_f" ? false : true,
      highlightColor: selectionType == "f_p" || selectionType == "f_f" ? Color.RED : Color.ORANGE,
    });

    setGlobalMode("none");

    const bboxRing = [
      { lon: west, lat: south },
      { lon: east, lat: south },
      { lon: east, lat: north },
      { lon: west, lat: north },
      { lon: west, lat: south },
    ];

    if (useMultiBoxCluster) {
      setMultiBoxClusterSelections((prev) => {
        const next = [...prev, { bounds, selection: res, bbox: bboxRing }];
        const mergedSelection = mergePPSelections(next.map((x) => x.selection));

        setLastSelectedWithRectangle(
          mergedSelection
            ? {
                ...mergedSelection,
                multiBoxCluster: true,
              }
            : null,
        );
        setDrawnBBOX(bboxRing);

        return next;
      });
    } else {
      if (!multiBoxClusterModeRef.current) {
        resetMultiBoxClusterState();
      } else {
        setMultiBoxClusterSelections([]);
      }
      setLastSelectedWithRectangle(res);
      setDrawnBBOX(bboxRing);
    }

    switch (selectionType) {
      case "p_p":
        {
          const sourceItems = useMultiBoxCluster
            ? Array.isArray(res?.results?.p_p)
              ? res.results.p_p
              : Object.values(res?.results?.p_p || {})
            : Array.isArray(res?.results?.p_p)
              ? res.results.p_p
              : Object.values(res?.results?.p_p || {});

          const coordinatesSet = new Set();

          for (const item of sourceItems) {
            const end = item?.endpoints?.end;
            const start = item?.endpoints?.start;

            if (end) {
              coordinatesSet.add(end.lat.toFixed(7) + " " + end.lon.toFixed(7));
            }
            if (start) {
              coordinatesSet.add(start.lat.toFixed(7) + " " + start.lon.toFixed(7));
            }
          }

          const uniqueCoordinates = Array.from(coordinatesSet);
          const coordinatesString = uniqueCoordinates.join("\n");

          showToast("Selected unique points: " + uniqueCoordinates.length, Type.NOTIFICATION.INFO);
          if (coordinatesString) showToast(coordinatesString, Type.NOTIFICATION.INFO);
        }
        break;
      case "p_f":
        {
          const coordinatesSet = new Set();

          for (const key in res?.results?.p_f) {
            for (const key2 in res?.results?.p_f[key].points) {
              const pt = res?.results?.p_f[key].points[key2];
              coordinatesSet.add(pt.lat.toFixed(7) + " " + pt.lon.toFixed(7));
            }
          }

          const uniqueCoordinates = Array.from(coordinatesSet);
          const coordinatesString = uniqueCoordinates.join("\n");

          showToast("Selected unique points: " + uniqueCoordinates.length, Type.NOTIFICATION.INFO);
          if (coordinatesString) showToast(coordinatesString, Type.NOTIFICATION.INFO);
        }
        break;
      case "f_p":
      case "f_f":
        {
          const newEntities = res?.results?.[selectionType];
          for (const key in newEntities) {
            highlightEntityForSelection(newEntities[key]);
          }
          if (newEntities?.length) {
            setSelectedEntities((prev) => {
              const byId = new Map();
              prev.forEach((e) => e && byId.set(e.id, e));
              newEntities.forEach((e) => e && byId.set(e.id, e));

              const result = Array.from(byId.values());

              if (selectedEntitiesRef) {
                selectedEntitiesRef.current = result;
              }

              return result;
            });
          }
        }
        break;
    }
  };

  // For Intersection Points
  useEffect(() => {
    const viewer = viewerRef.current;
    const ds = getIntersectionDataSourcesNow() || [];

    if (!viewer) return;

    if (viewer.__intersectionPointsRefreshRaf) {
      cancelAnimationFrame(viewer.__intersectionPointsRefreshRaf);
      viewer.__intersectionPointsRefreshRaf = null;
    }

    if (intersectionPointsEnabled) {
      viewer.__intersectionPointsRefresh = () => {
        if (!viewer || !useAppStore.getState().intersectionPointsEnabled) return;
        if (viewer.__intersectionPointsRefreshRaf) return;

        viewer.__intersectionPointsRefreshRaf = requestAnimationFrame(() => {
          viewer.__intersectionPointsRefreshRaf = null;
          const dsNow = getIntersectionDataSourcesNow() || [];
          highlightPolylineEndpointsAcrossDataSources({
            viewer,
            dataSources: dsNow,
            enabled: true,
          });
        });
      };

      highlightPolylineEndpointsAcrossDataSources({
        viewer,
        dataSources: ds,
        enabled: true,
      });
    } else {
      viewer.__intersectionPointsRefresh = null;

      highlightPolylineEndpointsAcrossDataSources({
        viewer,
        dataSources: ds,
        enabled: false,
      });
    }

    return () => {
      if (!viewer) return;
      if (viewer.__intersectionPointsRefreshRaf) {
        cancelAnimationFrame(viewer.__intersectionPointsRefreshRaf);
        viewer.__intersectionPointsRefreshRaf = null;
      }
      viewer.__intersectionPointsRefresh = null;
    };
  }, [intersectionPointsEnabled, intersectionPointsUseAll, intersectionDataSourcesAll, intersectionDataSourcesLimited]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    const clearTimer = () => {
      const st = intersectionAutoRef.current;
      if (st.tid) {
        clearTimeout(st.tid);
        st.tid = null;
      }
    };

    const getCameraCenterCartographic = () => {
      try {
        const pos = viewer.camera?.positionWC;
        if (!pos) return null;
        const c = Cartographic.fromCartesian(pos);
        c.height = 0;
        return c;
      } catch {
        return null;
      }
    };

    const surfaceDistanceMeters = (c1, c2) => {
      try {
        const a = new Cartographic(c1.longitude, c1.latitude, 0);
        const b = new Cartographic(c2.longitude, c2.latitude, 0);
        const g = new EllipsoidGeodesic(a, b);
        return g.surfaceDistance || 0;
      } catch {
        return 0;
      }
    };

    const refreshIntersection = () => {
      const ds = getIntersectionDataSourcesNow() || [];
      highlightPolylineEndpointsAcrossDataSources({ viewer, dataSources: ds, enabled: false });
      highlightPolylineEndpointsAcrossDataSources({ viewer, dataSources: ds, enabled: true });
    };

    const onCameraChanged = () => {
      if (!useAppStore.getState().intersectionPointsEnabled) return;

      clearTimer();

      intersectionAutoRef.current.tid = setTimeout(() => {
        if (!useAppStore.getState().intersectionPointsEnabled) return;

        const z = computeZoomLevel(viewer);
        if (z == null || z < 16) return;

        const cur = getCameraCenterCartographic();
        if (!cur) return;

        const st = intersectionAutoRef.current;
        if (!st.lastCenter) {
          st.lastCenter = cur;
          return;
        }

        const dist = surfaceDistanceMeters(st.lastCenter, cur);

        let threshold = 200;
        if (z >= 20) threshold = 20;
        else if (z >= 18) threshold = 50;
        else if (z >= 16) threshold = 200;

        if (dist <= threshold) return;

        refreshIntersection();
        st.lastCenter = cur;
      }, 2000);
    };

    if (!intersectionPointsEnabled) {
      clearTimer();
      intersectionAutoRef.current.lastCenter = null;
      return;
    }

    refreshIntersection();
    intersectionAutoRef.current.lastCenter = getCameraCenterCartographic();
    viewer.camera.changed.addEventListener(onCameraChanged);

    return () => {
      viewer.camera.changed.removeEventListener(onCameraChanged);
      clearTimer();
      intersectionAutoRef.current.lastCenter = null;
    };
  }, [intersectionPointsEnabled, intersectionPointsUseAll, intersectionDataSourcesAll, intersectionDataSourcesLimited]);

  // For Dangling Lines Points
  useEffect(() => {
    if (globalMode === "dangling_lines") {
      colorPolylinesBasedOnSharedEndpoints(viewerRef.current, true);
    } else {
      colorPolylinesBasedOnSharedEndpoints(viewerRef.current, false);
    }
  }, [globalMode]);

  // RightMenuGlobalTools.js – inside component
  const autoSnapInBounds = () => {
    showToast("Please select target point for snapping", Type.NOTIFICATION.INFO);
    const handler = registerIsInBoundingClick(viewerRef.current, ({ lat, lon, height, id }) => {
      const res = applyPPSelectionToCoordinate(viewerRef.current, lastSelectedWithRectangle, { lat: lat, lng: lon, height }, mapObjectsRef, setMapObjects, {}, userData);

      const undoItems = res?.undo || [];

      if (undoItems.length) {
        pushUndoAction({
          type: "auto_snap",
          undo: () => {
            undoItems.forEach((item) => {
              const { entity, type, positionsBefore } = item;
              if (!entity || !positionsBefore || !positionsBefore.length) return;

              const restored = positionsBefore.map((p) => Cartesian3.clone(p));

              if (type === "polyline" && entity.polyline) {
                entity.polyline.positions = new ConstantProperty(restored);
              } else if (type === "polygon" && entity.polygon) {
                if (typeof Cesium !== "undefined" && PolygonHierarchy) {
                  entity.polygon.hierarchy = new PolygonHierarchy(restored);
                } else if (typeof PolygonHierarchy !== "undefined") {
                  entity.polygon.hierarchy = new PolygonHierarchy(restored);
                } else {
                  entity.polygon.hierarchy = { positions: restored };
                }
              } else if (type === "point" && entity.position && restored[0]) {
                entity.position = new ConstantPositionProperty(restored[0]);
              }
            });
          },
        });
      }

      if (res.tooFar > 0) {
        showToast(`${res.tooFar} The points were more than 5 meters apart and the snap was not performed on them.`, Type.NOTIFICATION.WARNING);
      }

      resetHIBHighlights(viewerRef.current);
      handler.destroy();
      setCanOpenInfobox(true);
      setLastSelectedWithRectangle(null);
    });
  };

  const clickTimeout = useRef(null);
  const handleDeselectAllRef = useRef(null);
  useEffect(() => {
    return () => {
      if (clickTimeout.current) {
        clearTimeout(clickTimeout.current);
        clickTimeout.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const isMod = (k) => ["shift", "ctrl", "alt", "meta"].includes(String(k).toLowerCase());
    const isAlpha = (k) => /^[a-z]$/i.test(String(k || ""));

    const normalizeKey = (k) => {
      let key = String(k || "").toLowerCase();
      if (key === "control") key = "ctrl";
      if (key === " ") key = "space";
      if (key === "escape") key = "escape";
      return key;
    };

    const normalizePressedMain = (e) => {
      const code = String(e.code || "");

      // Digits: Shift+2 still maps to Digit2
      if (/^Digit\d$/.test(code)) return code.replace("Digit", "");

      // Stable for "=", "-" keys regardless of Shift
      if (code === "Equal") return "=";
      if (code === "Minus") return "-";

      // Numpad digits
      if (/^Numpad\d$/.test(code)) return code.replace("Numpad", "");

      return normalizeKey(e.key);
    };

    const getCombo = (type) => savedHotkeysRef.current.find((h) => h.type === type)?.combination || [];

    const matches = (e, combo) => {
      if (!combo || combo.length === 0) return false;

      const norm = combo.map(normalizeKey);

      const want = {
        shift: norm.includes("shift"),
        ctrl: norm.includes("ctrl"),
        alt: norm.includes("alt"),
        meta: norm.includes("meta"),
      };

      if (e.shiftKey !== want.shift) return false;
      if (e.ctrlKey !== want.ctrl) return false;
      if (e.altKey !== want.alt) return false;
      if (e.metaKey !== want.meta) return false;

      const main = norm.find((k) => !isMod(k));
      if (!main) return true;

      const pressed = normalizePressedMain(e);
      return pressed === main;
    };

    // -------------------------
    // 3-key sequential support
    // combo: [mod, letter1, letter2]
    // user: mod+letter1, then within 600ms press letter2 (with same mods or no mods)
    // -------------------------
    const PENDING_KEY = "__topology_hotkey_pending__";
    if (!window[PENDING_KEY]) {
      window[PENDING_KEY] = {
        active: false,
        type: null,
        mods: [],
        stepKey: "",
        finalKey: "",
        fallbackType: null,
        expiresAt: 0,
        tid: null,
        stepAt: 0,
      };
    }
    const pending = window[PENDING_KEY];

    const resetPending = () => {
      if (pending.tid) clearTimeout(pending.tid);
      pending.active = false;
      pending.type = null;
      pending.mods = [];
      pending.stepKey = "";
      pending.finalKey = "";
      pending.fallbackType = null;
      pending.expiresAt = 0;
      pending.tid = null;
      pending.stepAt = 0;
    };
    const modsMatchExactly = (e, mods) => {
      const needShift = mods.includes("shift");
      const needCtrl = mods.includes("ctrl");
      const needAlt = mods.includes("alt");
      const needMeta = mods.includes("meta");

      if (e.shiftKey !== needShift) return false;
      if (e.ctrlKey !== needCtrl) return false;
      if (e.altKey !== needAlt) return false;
      if (e.metaKey !== needMeta) return false;
      return true;
    };

    const modsAreNone = (e) => !e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey;

    const isSequential3Combo = (combo) => {
      if (!combo || combo.length !== 3) return false;

      const norm = combo.map(normalizeKey);
      const mods = norm.filter(isMod);
      const nonMods = norm.filter((k) => !isMod(k));

      return mods.length === 1 && nonMods.length === 2 && isAlpha(nonMods[0]) && isAlpha(nonMods[1]);
    };

    const getSequentialParts = (combo) => {
      const norm = combo.map(normalizeKey);
      const mods = norm.filter(isMod);
      const nonMods = norm.filter((k) => !isMod(k));
      return { mods, stepKey: nonMods[0], finalKey: nonMods[1] };
    };

    const startPending = (type, mods, stepKey, finalKey, fallbackType) => {
      resetPending();
      pending.active = true;
      pending.type = type;
      pending.mods = mods;
      pending.stepKey = stepKey;
      pending.finalKey = finalKey;
      pending.fallbackType = fallbackType || null;
      pending.stepAt = Date.now();

      const pendingMs = 600;

      pending.expiresAt = Date.now() + pendingMs;

      pending.tid = setTimeout(() => {
        if (pending.active && pending.type === type && pending.fallbackType) {
          const fb = pending.fallbackType;
          resetPending();

          if (type !== Type.HOTKEYS.SPLIT_AUTO) {
            runAction(fb, {
              preventDefault: () => {},
              repeat: false,
              stopPropagation: () => {},
              stopImmediatePropagation: () => {},
            });
          }

          return;
        }

        resetPending();
      }, pendingMs);
    };

    const runAction = (type, e) => {
      if (type === Type.HOTKEYS.TOPOLOGY_INTERSECTION_POINTS) {
        if (e.repeat) return true;
        e.preventDefault();

        // Shift+W => LIMITED
        setIntersectionPointsUseAll(false);
        setIntersectionPointsEnabled((v) => !v);

        return true;
      }

      if (type === Type.HOTKEYS.TOPOLOGY_INTERSECTION_POINTS_ALL) {
        if (e.repeat) return true;
        e.preventDefault();

        if (useAppStore.getState().intersectionPointsEnabled) {
          if (intersectionPointsUseAllRef.current) {
            setIntersectionPointsEnabled(false);
          } else {
            setIntersectionPointsUseAll(true);
          }
        } else {
          setIntersectionPointsUseAll(true);
          setIntersectionPointsEnabled(true);
        }

        return true;
      }

      if (type === Type.HOTKEYS.DESELECT) {
        e.preventDefault();
        deleteDrawingForSelect(viewerRef.current);
        restoreEntityColors(viewerRef.current);
        resetHIBHighlights(viewerRef.current);
        handleDeselectAllRef.current?.();
        setLastSelectedWithRectangle(null);
        resetMultiBoxClusterState();
        setIsEnableSwitchLaneletTurnDirection(false);
        return true;
      }

      if (type === Type.HOTKEYS.SWITCH_LANELET_SIDES) {
        e.preventDefault();
        handleSwitchLaneletSides();

        return true;
      }

      if (type === Type.HOTKEYS.SWITCH_LANELET_TURN_DIRECTION) {
        e.preventDefault();
        handleSwitchLaneletTurnDirection();

        return true;
      }

      if (type === Type.HOTKEYS.TOPOLOGY_DANGLING_LINES) {
        e.preventDefault();
        setGlobalMode((m) => (m === "dangling_lines" ? "none" : "dangling_lines"));
        return true;
      }

      if (type === Type.HOTKEYS.SELECT_RECTANGLE) {
        e.preventDefault();
        setGlobalMode((m) => (m === "select" ? "none" : "select"));
        return true;
      }

      if (type === Type.HOTKEYS.ALIGN_ENDPOINTS) {
        e.preventDefault();
        if (!userCanEditing) return true;

        if (!alignDataSourcesRef.current || alignDataSourcesRef.current.length === 0) {
          showToast("No valid layer datasource for Align.", Type.NOTIFICATION.WARNING);
          return true;
        }

        setGlobalMode((m) => (m === "align_endpoints" ? "none" : "align_endpoints"));
        return true;
      }

      if (type === Type.HOTKEYS.TOGGLE_AUTO_SNAP) {
        e.preventDefault();
        if (!userCanEditing) return true;
        handleAutoSnap();
        return true;
      }

      if (type === Type.HOTKEYS.AUTO_SNAP_CLUSTER) {
        e.preventDefault();
        if (!userCanEditing) return true;
        handleAutoSnapCluster();
        return true;
      }

      if (type === Type.HOTKEYS.SPLIT) {
        e.preventDefault();
        if (!userCanEditing) return true;

        if (useAppStore.getState().globalMode === "split" && splitSingleUseRef.current) {
          splitSingleUseRef.current = false;
          setGlobalMode("none");
        } else {
          splitSingleUseRef.current = true;
          setGlobalMode("split");
        }

        return true;
      }

      if (type === Type.HOTKEYS.SPLIT_AUTO) {
        e.preventDefault();
        if (!userCanEditing) return true;
        splitSingleUseRef.current = false;
        setGlobalMode("split");
        showToast("Split sticky mode enabled", Type.NOTIFICATION.INFO);
        return true;
      }

      if (type === Type.HOTKEYS.UNDO) {
        e.preventDefault();
        if (!userCanEditing) return true;
        undoLastCut(viewerRef.current, cutHistory, mapObjects, setMapObjects);
        return true;
      }

      if (type === Type.HOTKEYS.DELETE) {
        e.preventDefault();
        if (!userCanEditing) return true;
        try {
          handleRemoveAll();
        } catch (_) {}
        return true;
      }

      return false;
    };

    const ordered = [
      Type.HOTKEYS.TOPOLOGY_INTERSECTION_POINTS_ALL,
      Type.HOTKEYS.TOPOLOGY_INTERSECTION_POINTS,
      Type.HOTKEYS.DESELECT,
      Type.HOTKEYS.SWITCH_LANELET_SIDES,
      Type.HOTKEYS.SWITCH_LANELET_TURN_DIRECTION,

      Type.HOTKEYS.TOPOLOGY_DANGLING_LINES,
      Type.HOTKEYS.SELECT_RECTANGLE,
      Type.HOTKEYS.TOGGLE_AUTO_SNAP,
      Type.HOTKEYS.AUTO_SNAP_CLUSTER,
      Type.HOTKEYS.SPLIT_AUTO,
      Type.HOTKEYS.SPLIT,

      Type.HOTKEYS.UNDO,
      Type.HOTKEYS.DELETE,
      Type.HOTKEYS.ALIGN_ENDPOINTS,
    ];

    const handleKeyDown = (e) => {
      if (e.repeat) return;

      if (pending.active) {
        if (Date.now() > pending.expiresAt) {
          resetPending();
        } else {
          const k = normalizeKey(e.key);
          const sameMods = modsMatchExactly(e, pending.mods);
          const noMods = modsAreNone(e);

          if ((sameMods || noMods) && k === normalizeKey(pending.finalKey) && Date.now() >= pending.stepAt) {
            e.preventDefault();
            e.stopPropagation?.();
            e.stopImmediatePropagation?.();
            const type = pending.type;
            resetPending();
            runAction(type, e);
            return;
          }

          if (!isMod(k) && k !== normalizeKey(pending.stepKey)) {
            resetPending();
          }
        }
      }

      for (const type of ordered) {
        const combo = getCombo(type);

        if (isSequential3Combo(combo)) {
          const { mods, stepKey, finalKey } = getSequentialParts(combo);

          if (modsMatchExactly(e, mods) && normalizeKey(e.key) === normalizeKey(stepKey)) {
            e.preventDefault();
            e.stopPropagation?.();
            e.stopImmediatePropagation?.();

            const fallbackType = type === Type.HOTKEYS.SPLIT_AUTO ? Type.HOTKEYS.SPLIT : type === Type.HOTKEYS.TOPOLOGY_INTERSECTION_POINTS_ALL ? Type.HOTKEYS.TOPOLOGY_INTERSECTION_POINTS : null;

            startPending(type, mods, stepKey, finalKey, fallbackType);

            if (type === Type.HOTKEYS.SPLIT_AUTO && fallbackType) {
              runAction(fallbackType, e);
            }

            return;
          }
        }

        if (matches(e, combo)) {
          resetPending();
          runAction(type, e);
          return;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      resetPending();
    };
  }, [drawnBBOX, viewerRef, cutHistory, mapObjects, setMapObjects]);

  useEffect(() => {
    return () => {
      try {
        splitRef.current?.destroy();
      } catch {}
      try {
        selectRef.current?.destroy();
      } catch {}
      try {
        deleteDrawingForSelect(viewerRef.current);
      } catch {}
    };
  }, []);

  const alignRef = useRef(null);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    if (globalMode === "align_endpoints") {
      try {
        alignRef.current?.destroy?.();
      } catch {}

      toggleAlignEndpointsTool(
        viewer,
        false,
        alignDataSourcesRef.current,
        originalCameraControls,
        () => setGlobalMode("none"),
        (changedDataSources) => {
          if (!changedDataSources?.length) return;

          const changedSet = new Set(changedDataSources);

          const src = mapObjectsRef.current;
          if (!src) return;

          // mapObjects can be object or array in your codebase; handle both safely
          if (Array.isArray(src)) {
            src.forEach((mo) => {
              if (mo?.dataSource && changedSet.has(mo.dataSource)) {
                mo.is_edited = true;
              }
            });
            setMapObjects([...src]);
            return;
          }

          // object form
          for (const k in src) {
            const mo = src[k];
            if (mo?.dataSource && changedSet.has(mo.dataSource)) {
              mo.is_edited = true;
            }
          }
          setMapObjects({ ...src });
        },
        userData,
      );

      alignRef.current = { destroy: () => toggleAlignEndpointsTool(viewer, true) };
    } else {
      try {
        alignRef.current?.destroy?.();
      } catch {}
      alignRef.current = null;
    }
  }, [globalMode, originalCameraControls]);

  // Newwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwww

  const [isMovingMode, setIsMovingMode] = useState(false);
  const groupDragHandlerRef = useRef(null);

  const [isAutoSelecting, setIsAutoSelecting] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const handleAutoSelectClick = () => {
    const viewer = viewerRef.current;
    const result = toggleAutoSelect(viewer, {
      confirmNext,
      onNoNeighbor: handleNoNeighbor,
      onEntityAccepted: handleEntityAccepted,
    });

    if (result?.active) {
      setIsAutoSelecting(true);
      setCanOpenInfobox(false);
      setCanHightlightEntity(false);
      showToast("Selection Mode Active", Type.NOTIFICATION.INFO);
    }

    if (result?.finished) {
      setIsAutoSelecting(false);
      setCanOpenInfobox(true);
      setCanHightlightEntity(true);
      setConfirmState(null);
      showToast("Selection Mode Inactive", Type.NOTIFICATION.INFO);
    }
  };
  const confirmNext = ({ candidateEntity }) => {
    const viewer = viewerRef.current;
    if (viewer && candidateEntity) {
      flyToBBox(viewer, candidateEntity);
    }

    return new Promise((resolve) => {
      setConfirmState({
        candidateEntity,
        resolve,
      });
    });
  };

  const handleNoNeighbor = () => {
    showToast("No connected entity found. Please click another entity.", Type.NOTIFICATION.WARNING);
  };

  const handleEntityAccepted = (entity, entitiesArray) => {
    setSelectedEntities((prev) => {
      const byId = new Map();
      prev.forEach((e) => e && byId.set(e.id, e));
      entitiesArray.forEach((e) => e && byId.set(e.id, e));
      const result = Array.from(byId.values());
      if (selectedEntitiesRef) {
        selectedEntitiesRef.current = result;
      }
      return result;
    });
  };

  const handleConfirmYes = () => {
    if (confirmState?.resolve) {
      confirmState.resolve(true);
    }
    setConfirmState(null);
  };

  const restoreEntityStyle = (entity) => {
    const original = entity?._originalStyle;
    if (!original) return;

    if (entity.polygon && original.polygonMaterial) {
      entity.polygon.material = original.polygonMaterial;
    }

    if (entity.polyline) {
      if (original.polylineMaterial) {
        entity.polyline.material = original.polylineMaterial;
      }
      if (typeof original.polylineWidth === "number") {
        entity.polyline.width = original.polylineWidth;
      }
    }

    delete entity._originalStyle; // cleanup
  };

  const handleConfirmNo = () => {
    if (confirmState?.resolve) {
      confirmState.resolve(false);
    }
    setConfirmState(null);
  };

  const handleDeselectAll = () => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    if (!selectedEntities || selectedEntities.length === 0) return;

    selectedEntities.forEach((entity) => {
      removeSelectedEntity(viewer, entity); // Auto.js

      restoreEntityStyle(entity);
    });

    setSelectedEntities([]);
    if (selectedEntitiesRef) {
      selectedEntitiesRef.current = [];
    }

    showToast("Selection cleared", Type.NOTIFICATION.INFO);
  };
  handleDeselectAllRef.current = handleDeselectAll;

  const handleRemoveEntity = (entity) => {
    const viewer = viewerRef.current;

    removeSelectedEntity(viewer, entity);
    restoreEntityStyle(entity);

    setSelectedEntities((prev) => {
      const next = prev.filter((e) => e.id !== entity.id);
      if (selectedEntitiesRef) {
        selectedEntitiesRef.current = next;
      }
      return next;
    });
  };

  const handleRemoveAll = () => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    const entities = selectedEntitiesRef?.current ?? [];
    const hasRectangleSelection = !!lastSelectedWithRectangle;

    // 1) Rectangle-based delete (if exists)
    if (hasRectangleSelection) {
      const result = deleteSelectionFromViewer(viewer, lastSelectedWithRectangle, mapObjectsRef, setMapObjects);

      const undoItems = result?.undo ?? [];

      if (undoItems.length) {
        pushUndoAction({
          type: "delete_selection",
          undo: () => {
            undoItems.forEach((item) => {
              if (!item) return;

              if (item.kind === "remove_entity") {
                if (item.owner && item.entity && !item.owner.contains(item.entity)) {
                  item.owner.add(item.entity);
                }
                return;
              }

              if (item.kind === "modify_geometry") {
                const { entity, type, positionsBefore } = item;
                if (!entity || !Array.isArray(positionsBefore)) return;

                if (type === "polyline" && entity.polyline) {
                  entity.polyline.positions = positionsBefore;
                } else if (type === "polygon" && entity.polygon) {
                  const PH = PolygonHierarchy;

                  entity.polygon.hierarchy = PH ? new PH(positionsBefore) : { positions: positionsBefore };
                } else if (type === "point" && positionsBefore[0]) {
                  entity.position = positionsBefore[0];
                }
              }
            });
          },
        });
      }

      resetHIBHighlights(viewer);
      deleteDrawingForSelect(viewer);
      restoreEntityColors(viewer);
      setLastSelectedWithRectangle(null);
      resetMultiBoxClusterState();
    }

    // 2) Remove selected entities (if any)
    if (!entities.length) return;

    const removed = entities.map((ent) => ({
      entity: ent,
      collection: ent?.entityCollection,
    }));

    removeEntities(viewer, entities);

    pushUndoAction({
      type: "delete_features",
      undo: () => {
        removed.forEach(({ entity, collection }) => {
          if (!entity || !collection) return;
          if (!collection.contains(entity)) collection.add(entity);
        });

        const restored = removed.map((r) => r.entity).filter(Boolean);
        setSelectedEntities(restored);
        if (selectedEntitiesRef) selectedEntitiesRef.current = restored;
      },
    });

    setSelectedEntities([]);
    if (selectedEntitiesRef) selectedEntitiesRef.current = [];
  };

  const handleMerge = async () => {
    const viewer = viewerRef.current;

    if (!viewer) return;
    if (!selectedEntitiesRef.current && !lastSelectedWithRectangle) return;

    const selectedSnapshot = [...(selectedEntitiesRef?.current || [])];

    const res = await mergeEntities(viewer, lastSelectedWithRectangle, mapObjectsRef, setMapObjects, selectedSnapshot, userData);
    if (!res || !res.undo) return;

    const { mutated, removed } = res.undo;

    pushUndoAction({
      type: "merge",
      undo: () => {
        mutated.forEach((item) => {
          const { entity, pointsBefore, propertiesBefore } = item;
          if (!entity || !pointsBefore || !pointsBefore.length) return;
          const flat = [];
          pointsBefore.forEach((p) => {
            flat.push(p.x, p.y, p.z ?? 0);
          });
          const cartesians = Cartesian3.fromDegreesArrayHeights(flat);
          if (entity.polyline) {
            entity.polyline.positions = cartesians;
          } else {
            entity.polyline = { positions: cartesians };
          }
          if (typeof propertiesBefore !== "undefined") {
            entity.properties = propertiesBefore;
          }
        });

        removed.forEach((item) => {
          const { entity, dataSource } = item;
          if (!entity || !dataSource || !dataSource.entities) return;
          if (!dataSource.entities.contains(entity)) {
            dataSource.entities.add(entity);
          }
        });

        setMapObjects([...mapObjectsRef.current]);
      },
    });

    selectedSnapshot.forEach((entity) => {
      if (!entity) return;
      try {
        removeSelectedEntity(viewer, entity);
      } catch {}
      restoreEntityStyle(entity);
    });

    if (lastSelectedWithRectangle) {
      resetHIBHighlights(viewer);
      deleteDrawingForSelect(viewer);
      restoreEntityColors(viewer);
      setLastSelectedWithRectangle(null);
      resetMultiBoxClusterState();
    }

    setSelectedEntities([]);
    if (selectedEntitiesRef) {
      selectedEntitiesRef.current = [];
    }
  };

  const handleReverseWaypoints = () => {
    const viewer = viewerRef.current;
    const entities = selectedEntitiesRef?.current || [];

    if (!viewer || entities.length === 0) return;

    // snapshot of entities for undo
    const entitiesSnapshot = [...entities];

    // do reverse now
    reverseWaypointsEntities(viewerRef.current, selectedEntitiesRef, mapObjectsRef, setMapObjects, directionArrowSettingRef);

    // push undo action
    pushUndoAction({
      type: "reverse_waypoints",
      undo: () => {
        const v = viewerRef.current;
        if (!v || !entitiesSnapshot.length) return;

        // apply reverse again on same entities
        reverseWaypointsEntities(
          v,
          selectedEntitiesRef,
          mapObjectsRef,
          setMapObjects,
          directionArrowSettingRef,
          entitiesSnapshot, // force same entities
        );

        // optional: restore selection
        setSelectedEntities?.(entitiesSnapshot);
        if (selectedEntitiesRef) {
          selectedEntitiesRef.current = entitiesSnapshot;
        }
      },
    });
  };

  const handleDuplicate = () => {
    const viewer = viewerRef.current;
    const entities = selectedEntitiesRef?.current || [];

    if (!viewer || entities.length === 0) return;

    const result = duplicateEntities(selectedEntitiesRef);
    const created = result?.created || [];

    if (!created.length) return;

    pushUndoAction({
      type: "duplicate",
      undo: () => {
        const v = viewerRef.current;
        if (!v) return;

        created.forEach((ent) => {
          if (!ent) return;
          const coll = ent.entityCollection;
          if (coll && coll.contains && coll.contains(ent)) {
            coll.remove(ent);
          } else if (coll && coll.remove) {
            try {
              coll.remove(ent);
            } catch {}
          }
        });
      },
    });
  };

  const handleEditTags = () => {
    const viewer = viewerRef.current;
    const entities = selectedEntitiesRef?.current || [];

    if (!viewer || entities.length === 0) return;

    setBultEditDialog(true);
    setSelectedFeatures(selectedEntitiesRef.current);
  };

  const moveSnapshotRef = useRef(null);
  const handleMoving = () => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    const entities = selectedEntitiesRef?.current || selectedEntities || [];

    // turning OFF move mode
    if (isMovingMode) {
      if (groupDragHandlerRef.current) {
        groupDragHandlerRef.current.destroy();
        groupDragHandlerRef.current = null;
      }

      const snapshot = moveSnapshotRef.current;
      if (snapshot && snapshot.length) {
        const undoPayload = snapshot.map((item) => ({
          entity: item.entity,
          type: item.type,
          position: item.position ? Cartesian3.clone(item.position) : null,
          positions: item.positions ? item.positions.map((p) => Cartesian3.clone(p)) : null,
        }));

        pushUndoAction({
          type: "move",
          undo: () => {
            undoPayload.forEach((item) => {
              const { entity, type } = item;
              if (!entity) return;

              if (type === "position" && item.position) {
                entity.position = new ConstantPositionProperty(Cartesian3.clone(item.position));
              } else if (type === "polyline" && item.positions && item.positions.length) {
                entity.polyline.positions = new ConstantProperty(item.positions.map((p) => Cartesian3.clone(p)));
              }
            });

            setMapObjects?.([...mapObjectsRef.current]);
            const restoredEntities = snapshot.map((s) => s.entity).filter(Boolean);
            setSelectedEntities?.(restoredEntities);
            if (selectedEntitiesRef) {
              selectedEntitiesRef.current = restoredEntities;
            }
          },
        });
      }

      moveSnapshotRef.current = null;
      restoreCameraControls(viewer, originalCameraControls);
      setIsMovingMode(false);
      showToast("Move mode disabled", Type.NOTIFICATION.INFO);
      return;
    }

    // turning ON move mode
    if (!entities.length) return;

    const time = viewer.clock.currentTime;
    const before = [];

    entities.forEach((ent) => {
      if (!ent) return;

      // position entity
      if (ent.position) {
        let pos;
        if (typeof ent.position.getValue === "function") {
          pos = ent.position.getValue(time);
        } else {
          pos = ent.position;
        }
        if (pos) {
          before.push({
            entity: ent,
            type: "position",
            position: Cartesian3.clone(pos),
          });
          return;
        }
      }

      // polyline entity
      if (ent.polyline && ent.polyline.positions) {
        const posProp = ent.polyline.positions;
        let positions;
        if (typeof posProp.getValue === "function") {
          positions = posProp.getValue(time);
        } else {
          positions = posProp;
        }
        if (positions && positions.length) {
          before.push({
            entity: ent,
            type: "polyline",
            positions: positions.map((p) => Cartesian3.clone(p)),
          });
        }
      }
    });

    if (!before.length) return;

    moveSnapshotRef.current = before;

    const handler = enableGroupDrag(viewer, [...entities], originalCameraControls, userData);
    groupDragHandlerRef.current = handler;
    setIsMovingMode(true);
    showToast("Move mode enabled", Type.NOTIFICATION.INFO);
  };

  const handleRearrangePoints = () => {
    const viewer = viewerRef.current;
    const entities = selectedEntitiesRef?.current || selectedEntities || [];

    if (!viewer || !entities.length) return;

    const result = rearrangePoints(entities, simplifyToleranceSettingRef.current?.rearrange, setMapObjects, {}, userData);

    const undoItems = result?.undo || [];
    if (undoItems.length) {
      pushUndoAction({
        type: "rearrange_points",
        undo: () => {
          undoItems.forEach((item) => {
            const { entity, oldPositions } = item;
            if (!entity || !oldPositions || !oldPositions.length) return;

            const restored = oldPositions.map((p) => Cartesian3.clone(p));
            entity.polyline.positions = new ConstantProperty(restored);
          });

          setMapObjects?.([...mapObjectsRef.current]);

          const ents = undoItems.map((u) => u.entity).filter(Boolean);
          setSelectedEntities?.(ents);
          if (selectedEntitiesRef) {
            selectedEntitiesRef.current = ents;
          }
        },
      });
    }

    showToast("Rearranged points", Type.NOTIFICATION.SUCCESS);
  };

  const handleAutoSnap = () => {
    if (!lastSelectedWithRectangle?.results?.p_p?.length) {
      return;
    }
    autoSnapInBounds(drawnBBOX, viewerRef.current);

    deleteDrawingForSelect(viewerRef.current);
    restoreEntityColors(viewerRef.current);
    setCanOpenInfobox(false);
  };

  const handleAutoSnapCluster = () => {
    const viewer = viewerRef.current;

    const sourceSelections =
      multiBoxClusterModeRef.current && multiBoxClusterSelections.length
        ? multiBoxClusterSelections.map((x) => x.selection).filter(Boolean)
        : lastSelectedWithRectangle
          ? [lastSelectedWithRectangle]
          : [];

    if (!viewer || !sourceSelections.length) return;

    const TH = 1.5;
    const allUndo = [];
    let tooFarTotal = 0;
    let foundAnyCluster = false;

    const distMeters = (a, b) => {
      try {
        const c1 = Cartographic.fromDegrees(a.lon, a.lat);
        const c2 = Cartographic.fromDegrees(b.lon, b.lat);
        const g = new EllipsoidGeodesic(c1, c2);
        return Number(g.surfaceDistance || 0);
      } catch {
        return Infinity;
      }
    };

    const mkId = (item, point, which) => {
      const key = item?.key ?? item?.id ?? item?.entity?.id ?? item?.entityId ?? item?.name ?? "k";
      return `${Number(point.lat).toFixed(7)} ${Number(point.lon).toFixed(7)} ${String(key)}_${which}`;
    };

    for (const selection of sourceSelections) {
      const res = selection?.results;
      const pp = res?.p_p;
      if (!pp) continue;

      const items = Array.isArray(pp) ? pp : Object.values(pp);
      if (!items.length) continue;

      const pts = [];

      for (const it of items) {
        const group = getSnapLayerGroup(it);
        if (group === "other") continue;

        const endpoints = it?.endpoints || {};
        const start = endpoints?.start;
        const end = endpoints?.end;

        if (start && Number.isFinite(start.lat) && Number.isFinite(start.lon)) {
          pts.push({
            id: mkId(it, start, "s"),
            lat: Number(start.lat),
            lon: Number(start.lon),
            height: Number(start.height ?? start.z ?? 0),
            layerGroup: group,
          });
        }

        if (end && Number.isFinite(end.lat) && Number.isFinite(end.lon)) {
          pts.push({
            id: mkId(it, end, "e"),
            lat: Number(end.lat),
            lon: Number(end.lon),
            height: Number(end.height ?? end.z ?? 0),
            layerGroup: group,
          });
        }
      }

      if (pts.length < 2) continue;

      const parent = Array.from({ length: pts.length }, (_, i) => i);
      const find = (x) => {
        while (parent[x] !== x) {
          parent[x] = parent[parent[x]];
          x = parent[x];
        }
        return x;
      };
      const union = (a, b) => {
        const ra = find(a);
        const rb = find(b);
        if (ra !== rb) parent[rb] = ra;
      };

      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          if (pts[i].layerGroup !== pts[j].layerGroup) continue;
          if (distMeters(pts[i], pts[j]) <= TH) union(i, j);
        }
      }

      const groups = new Map();
      for (let i = 0; i < pts.length; i++) {
        const r = find(i);
        if (!groups.has(r)) groups.set(r, []);
        groups.get(r).push(pts[i]);
      }

      const clusters = Array.from(groups.values()).filter((g) => g.length >= 2);
      if (!clusters.length) continue;

      foundAnyCluster = true;
      const originalPpObj = Array.isArray(pp) ? null : pp;

      for (const g of clusters) {
        let sumLat = 0;
        let sumLon = 0;
        let sumH = 0;

        for (const p of g) {
          sumLat += p.lat;
          sumLon += p.lon;
          sumH += p.height;
        }

        const target = {
          lat: sumLat / g.length,
          lng: sumLon / g.length,
          height: sumH / g.length,
        };

        const endpointSet = new Set(g.map((p) => p.id));

        let subsetPp;
        if (Array.isArray(pp)) {
          subsetPp = pp.map((it) => {
            const group = getSnapLayerGroup(it);
            const endpoints = it?.endpoints || {};
            const start = endpoints?.start;
            const end = endpoints?.end;

            const startId = start && Number.isFinite(start.lat) && Number.isFinite(start.lon) ? mkId(it, start, "s") : null;
            const endId = end && Number.isFinite(end.lat) && Number.isFinite(end.lon) ? mkId(it, end, "e") : null;

            const keepStart = group === g[0].layerGroup && startId && endpointSet.has(startId);
            const keepEnd = group === g[0].layerGroup && endId && endpointSet.has(endId);

            return {
              ...it,
              endpoints: {
                ...endpoints,
                start: keepStart ? start : null,
                end: keepEnd ? end : null,
              },
            };
          });
        } else {
          subsetPp = {};
          for (const k in originalPpObj) {
            const it = originalPpObj[k];
            const group = getSnapLayerGroup(it);
            const endpoints = it?.endpoints || {};
            const start = endpoints?.start;
            const end = endpoints?.end;

            const startId = start && Number.isFinite(start.lat) && Number.isFinite(start.lon) ? mkId(it, start, "s") : null;
            const endId = end && Number.isFinite(end.lat) && Number.isFinite(end.lon) ? mkId(it, end, "e") : null;

            const keepStart = group === g[0].layerGroup && startId && endpointSet.has(startId);
            const keepEnd = group === g[0].layerGroup && endId && endpointSet.has(endId);

            subsetPp[k] = {
              ...it,
              endpoints: {
                ...endpoints,
                start: keepStart ? start : null,
                end: keepEnd ? end : null,
              },
            };
          }
        }

        const subsetSelection = {
          ...selection,
          results: {
            ...res,
            p_p: subsetPp,
          },
        };

        const r = applyPPSelectionToCoordinate(viewer, subsetSelection, target, mapObjectsRef, setMapObjects, {}, userData);

        const undoItems = r?.undo || [];
        if (undoItems.length) allUndo.push(...undoItems);
        if (r?.tooFar) tooFarTotal += Number(r.tooFar || 0);
      }
    }

    if (!foundAnyCluster) {
      showToast("No close groups found", Type.NOTIFICATION.INFO);
      return;
    }

    if (allUndo.length) {
      pushUndoAction({
        type: "auto_snap_cluster",
        undo: () => {
          allUndo.forEach((item) => {
            const { entity, type, positionsBefore } = item;
            if (!entity || !positionsBefore || !positionsBefore.length) return;

            const restored = positionsBefore.map((p) => Cartesian3.clone(p));

            if (type === "polyline" && entity.polyline) {
              entity.polyline.positions = new ConstantProperty(restored);
            } else if (type === "polygon" && entity.polygon) {
              if (typeof Cesium !== "undefined" && PolygonHierarchy) {
                entity.polygon.hierarchy = new PolygonHierarchy(restored);
              } else if (typeof PolygonHierarchy !== "undefined") {
                entity.polygon.hierarchy = new PolygonHierarchy(restored);
              } else {
                entity.polygon.hierarchy = { positions: restored };
              }
            } else if (type === "point" && entity.position && restored[0]) {
              entity.position = new ConstantPositionProperty(restored[0]);
            }
          });
        },
      });
    }

    if (tooFarTotal > 0) {
      showToast(`${tooFarTotal} The points were more than 5 meters apart and the snap was not performed on them.`, Type.NOTIFICATION.WARNING);
    }

    resetHIBHighlights(viewer);
    deleteDrawingForSelect(viewer);
    restoreEntityColors(viewer);
    setCanOpenInfobox(true);
    setLastSelectedWithRectangle(null);
    resetMultiBoxClusterState();

    showToast("Auto snap done", Type.NOTIFICATION.SUCCESS);
  };

  const handleChangeHeight = () => {
    setGlobalToolsOpen(false);
    const res = lastSelectedWithRectangle?.results;

    const ppCount = Array.isArray(res?.p_p) ? res.p_p.length : res?.p_p ? Object.keys(res.p_p).length : 0;
    const pfCount = Array.isArray(res?.p_f) ? res.p_f.length : res?.p_f ? Object.keys(res.p_f).length : 0;

    if (!ppCount && !pfCount) return;

    let firstPoint = null;

    if (ppCount && res?.p_p) {
      const k = Array.isArray(res.p_p) ? 0 : Object.keys(res.p_p)[0];
      const item = Array.isArray(res.p_p) ? res.p_p[0] : res.p_p[k];
      firstPoint = item?.endpoints?.start || item?.endpoints?.end || item?.start || item?.end;
    }

    if (!firstPoint && pfCount && res?.p_f) {
      const k = Array.isArray(res.p_f) ? 0 : Object.keys(res.p_f)[0];
      const item = Array.isArray(res.p_f) ? res.p_f[0] : res.p_f[k];
      const pts = item?.points;

      if (Array.isArray(pts)) {
        firstPoint = pts[0];
      } else if (pts && typeof pts === "object") {
        const k2 = Object.keys(pts)[0];
        firstPoint = pts[k2];
      }
    }

    const position =
      firstPoint?.cartesian ||
      firstPoint?.position ||
      (Number.isFinite(firstPoint?.lon) && Number.isFinite(firstPoint?.lat) ? Cartesian3.fromDegrees(firstPoint.lon, firstPoint.lat, Number(firstPoint?.height ?? firstPoint?.z ?? 0)) : null);

    setEditCoordianteModalData({
      mode: "bulk_height",
      selection: lastSelectedWithRectangle,
      position,
    });

    setIsOpenEditCoordianteModal(true);
  };

  useEffect(() => {
    if (!isMovingMode) return;

    if (!selectedEntities || selectedEntities.length === 0) {
      const viewer = viewerRef.current;

      if (groupDragHandlerRef.current) {
        groupDragHandlerRef.current.destroy();
        groupDragHandlerRef.current = null;
      }

      if (viewer) {
        restoreCameraControls(viewer, originalCameraControls);
      }

      setIsMovingMode(false);
      showToast("Move mode disabled (no entities selected)", Type.NOTIFICATION.INFO);
    }
  }, [isMovingMode, selectedEntities, viewerRef, originalCameraControls]);

  useEffect(() => {
    if (!selectedEntities || selectedEntities.length === 0) {
      restoreCameraControls(viewerRef.current, originalCameraControls);
      setIsMovingMode(false);
    }
  }, [selectedEntities]);

  const handleUndo = () => {
    const action = popUndoAction();
    if (action && typeof action.undo === "function") {
      action.undo();
      return;
    }
  };

  useEffect(() => {
    let temp = true;
    for (const key in selectedEntities) {
      const entity = selectedEntities[key];
      const viewer = viewerRef.current;
      const list = viewer?.dataSources?._dataSources || [];
      for (const ds of list) {
        if (ds?.entities?.contains?.(entity)) {
          if (ds.layerName.toLowerCase().includes("lanelet")) {
            temp = false;
          }
        }
      }
    }
    setIsDisableSwitchLaneletSides(temp);
  }, [selectedEntities]);

  const buildDataSources = () => {
    let main = [];
    let side = [];

    for (const key in mapObjects) {
      const obj = mapObjects[key];
      if (!obj) continue;

      const fileName = obj.file_name || "";
      const ds = obj.dataSource;
      if (!ds) continue;

      if (matchKeyInText(fileName, "lanelet")) {
        main.push(ds);
      }

      if (matchKeyInText(fileName, "lanemarker") || matchKeyInText(fileName, "virtualline") || matchKeyInText(fileName, "turningline")) {
        side.push(ds);
      }
    }

    return { main, side };
  };

  const handleSwitchLaneletSides = () => {
    const viewer = viewerRef.current;
    const list = viewer?.dataSources?._dataSources || [];
    const { main, side } = buildDataSources();

    let tempEntities = [];
    for (const key in selectedEntities) {
      const entity = selectedEntities[key];
      for (const ds of list) {
        if (ds?.entities?.contains?.(entity)) {
          if (ds.layerName.toLowerCase().includes("lanelet")) {
            tempEntities.push(selectedEntities[key]);
            break;
          }
        }
      }
    }

    for (const key in tempEntities) {
      const entity = tempEntities[key];
      const id = getPropertyValue(entity, "id");
      const result = findLaneletEntitiesForId(id, main, side);
      switchLeftRightLines(result, userData);
    }
    showToast("Selected lanelet lines were swapped.", Type.NOTIFICATION.SUCCESS);
  };

  const handleSwitchLaneletTurnDirection = () => {
    const tempIsEnable = !isEnableSwitchLaneletTurnDirection;
    setIsEnableSwitchLaneletTurnDirection(tempIsEnable);

    const handleKeyPress = (e) => {
      const key = e.key.toLowerCase();
      if (["r", "l", "s"].includes(key)) {
        window.removeEventListener("keydown", handleKeyPress);
        for (const key2 in tempEntities) {
          const entity = tempEntities[key2];
          const props = entity?.properties;
          let value = "right";
          switch (key) {
            case "r":
              value = "right";
              break;
            case "l":
              value = "left";
              break;
            case "s":
              value = "straight";
              break;
          }
          setPropertyValue(props, "turn_direction", value);
        }

        showToast("Done.", Type.NOTIFICATION.SUCCESS);
        setIsEnableSwitchLaneletTurnDirection(false);
      }
    };

    const viewer = viewerRef.current;
    const list = viewer?.dataSources?._dataSources || [];

    let tempEntities = [];
    for (const key in selectedEntities) {
      const entity = selectedEntities[key];
      for (const ds of list) {
        if (ds?.entities?.contains?.(entity)) {
          if (ds.layerName.toLowerCase().includes("lanelet")) {
            tempEntities.push(selectedEntities[key]);
            break;
          }
        }
      }
    }

    if (tempEntities.length > 0 && tempIsEnable) {
      window.addEventListener("keydown", handleKeyPress);
      showToast("Enter R, L, S.", Type.NOTIFICATION.INFO);
    } else {
      window.removeEventListener("keydown", handleKeyPress);
    }
  };

  return (
    <>
      <Popover.Root
        positioning={{ placement: "left-start" }}
        open={globalToolsOpen}
        onOpenChange={(e) => {
          setGlobalToolsOpen(e.open);
          if (!e.open) setIsSticky(false);
        }}
        closeOnInteractOutside={!isSticky}
      >
        <Popover.Trigger asChild>
          <Button
            userSelect={"none"}
            p={0}
            bg={"Background"}
            onClickCapture={(e) => {
              e.preventDefault();
              e.stopPropagation();

              if (clickTimeout.current) {
                clearTimeout(clickTimeout.current);
                clickTimeout.current = null;
                setIsSticky(true);
                setGlobalToolsOpen(true);
              } else {
                clickTimeout.current = setTimeout(() => {
                  setIsSticky(false);
                  setGlobalToolsOpen((prev) => !prev);
                  clickTimeout.current = null;
                }, 220);
              }
            }}
          >
            <Tooltip content="Global Tools">
              <LuLassoSelect color="MenuText" />
            </Tooltip>
            {(selectedEntities?.length ?? 0) !== 0 ||
            (lastSelectedWithRectangle?.results?.p_p?.length ?? 0) > 0 ||
            (lastSelectedWithRectangle?.results?.p_f?.length ?? 0) > 0 ||
            isMultiBoxClusterMode ? (
              <Float placement={"middle-start"}>
                <Box position="relative">
                  <Circle size="5" bg="red" color="white">
                    {(selectedEntities?.length ?? 0) + (lastSelectedWithRectangle?.results?.p_p?.length ?? 0) + (lastSelectedWithRectangle?.results?.p_f?.length ?? 0)}
                  </Circle>
                  {isMultiBoxClusterMode ? <Circle size="2" bg="green.400" position="absolute" top="-1" right="-1" border="1px solid white" /> : null}
                </Box>
              </Float>
            ) : null}
          </Button>
        </Popover.Trigger>
        <Portal>
          <Popover.Positioner>
            <Popover.Content width={"fit-content"} userSelect={"none"}>
              <Popover.Arrow />
              <Popover.Body p={2}>
                <HStack mb={2} justify={"space-between"}>
                  <Heading fontSize={"lg"}>Global Tools</Heading>

                  <CloseButton
                    onClick={() => {
                      setIsSticky(false);
                      setGlobalToolsOpen(false);
                    }}
                  />
                </HStack>
                <Separator mb={1} />
                <SimpleGrid column={1} spacing={1}>
                  <VStack spacing={0}>
                    <Text width={"full"} fontWeight={"medium"} fontSize={"md"} mt={1}>
                      Selection Tools
                    </Text>
                    <RadioGroup.Root
                      value={selectionType}
                      width={"full"}
                      onValueChange={(v) => {
                        selectionTypeFunction(v.value);
                      }}
                    >
                      <SimpleGrid columns={2} gap={2} justifyItems={"left"} alignItems={"start"}>
                        {selectionTypeOptions.map((item) => (
                          <RadioGroup.Item key={item.value} value={item.value} cursor={"pointer"}>
                            <RadioGroup.ItemHiddenInput />
                            <RadioGroup.ItemIndicator cursor={"pointer"} />
                            <RadioGroup.ItemText cursor={"pointer"}>{item.label}</RadioGroup.ItemText>
                          </RadioGroup.Item>
                        ))}
                      </SimpleGrid>
                    </RadioGroup.Root>
                    <HStack justify={"left"} justifyContent={"left"} width={"full"}>
                      <Button
                        px={0}
                        gap={1}
                        height={"25px"}
                        justifyContent={"left"}
                        onClick={() => {
                          if (globalMode == "select") {
                            setGlobalMode("none");
                          } else {
                            setGlobalMode("select");
                          }
                        }}
                        pr={1}
                        bg={globalMode == "select" ? "green" : "transparent"}
                        color={globalMode == "select" ? "white" : "MenuText"}
                        py={0}
                        my={0}
                        mb={1}
                      >
                        <LuSquare /> Rectangle {getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.SELECT_RECTANGLE)}
                      </Button>

                      <Button
                        gap={1}
                        px={0}
                        height={"25px"}
                        justifyContent={"left"}
                        pr={1}
                        bg={isAutoSelecting ? "green" : "transparent"}
                        color={isAutoSelecting ? "white" : "MenuText"}
                        py={0}
                        my={0}
                        mb={1}
                        onClick={handleAutoSelectClick}
                      >
                        <LuSquareFunction /> {isAutoSelecting ? "Finish Auto Select" : "Auto Select"}
                      </Button>
                      <Button
                        px={0}
                        height={"25px"}
                        justifyContent={"left"}
                        gap={1}
                        onClick={() => {
                          deleteDrawingForSelect(viewerRef.current);
                          restoreEntityColors(viewerRef.current);
                          resetHIBHighlights(viewerRef.current);
                          handleDeselectAll();
                          setLastSelectedWithRectangle(null);
                          resetMultiBoxClusterState();
                          setIsEnableSwitchLaneletTurnDirection(false);
                        }}
                        pr={1}
                        bg={"transparent"}
                        color={"red"}
                        py={0}
                        my={0}
                        mb={1}
                      >
                        <LuX /> Deselect {getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.DESELECT)}
                      </Button>
                    </HStack>
                  </VStack>

                  <Box p={2} bg={"blackAlpha.100"} borderRadius={"xl"} mb={2}>
                    {selectedEntities.length != 0 && <Text mt={2}>Selected features:</Text>}
                    {selectedEntities.length == 0 && <Text>No feature is selected</Text>}
                    <SimpleGrid columns={2} gap={2} maxHeight={"300px"} overflowY={"scroll"} px={2}>
                      {selectedEntities.map((entity, index) => {
                        const currentTime = viewerRef.current?.clock.currentTime;
                        const propId = entity?.properties?.id;
                        const propIdValue = propId && typeof propId.getValue === "function" ? propId.getValue(currentTime) : propId;

                        return (
                          <HStack key={entity.id || index} spacing={2} justify="space-between" bg={"blackAlpha.200"} borderRadius={"xl"} px={2}>
                            <Text fontSize="xs" whiteSpace="nowrap">
                              {propIdValue ?? `Entity ${index + 1}`}
                            </Text>
                            <Button size="xs" bg={"transparent"} outline={"none"} onClick={() => handleRemoveEntity(entity)}>
                              <LuX color="red" />
                            </Button>
                          </HStack>
                        );
                      })}
                    </SimpleGrid>
                  </Box>
                  <Box p={2} bg={"blackAlpha.100"} borderRadius={"xl"} mb={2}>
                    {lastSelectedWithRectangle?.results?.p_p?.length || lastSelectedWithRectangle?.results?.p_f?.length ? <Text mt={2}>Selected points:</Text> : null}
                    {!lastSelectedWithRectangle?.results?.p_p?.length && !lastSelectedWithRectangle?.results?.p_f?.length && <Text>No point is selected</Text>}
                    <SimpleGrid columns={4} gap={2} maxHeight={"300px"} overflowY={"scroll"} px={2}>
                      {lastSelectedWithRectangle?.results?.p_p?.map((entity, index) => {
                        return (
                          <HStack key={index} spacing={2} justify="space-between" bg={"blackAlpha.200"} borderRadius={"xl"} px={2}>
                            <Text fontSize="xs" whiteSpace="nowrap">
                              Point {index}
                            </Text>
                          </HStack>
                        );
                      })}
                      {lastSelectedWithRectangle?.results?.p_f?.map((entity, index) => {
                        return (
                          <HStack key={index} spacing={2} justify="space-between" bg={"blackAlpha.200"} borderRadius={"xl"} px={2}>
                            <Text fontSize="xs" whiteSpace="nowrap">
                              Point {index}
                            </Text>
                          </HStack>
                        );
                      })}
                    </SimpleGrid>
                  </Box>
                  <Separator mb={1} />

                  {userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.DIGITIZER) || userType.includes(Type.ROLE.SUPER_ADMIN) ? (
                    <>
                      <VStack spacing={0} width={"full"}>
                        <Text width={"full"} fontWeight={"medium"} fontSize={"md"} mt={1}>
                          General Tools
                        </Text>
                        <SimpleGrid columns={9} gap={2} my={2} mt={0} width={"full"}>
                          <Button
                            colorPalette={"red"}
                            p={0}
                            borderRadius="full"
                            disabled={selectedEntities.length == 0 && !lastSelectedWithRectangle?.results?.p_p?.length && !lastSelectedWithRectangle?.results?.p_f?.length ? true : false}
                            onClick={handleRemoveAll}
                          >
                            <Tooltip content={"Remove " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.DELETE)}>
                              <LuTrash />
                            </Tooltip>
                          </Button>
                          <Button colorPalette={"MenuText"} p={0} borderRadius="full" disabled={selectedEntities.length == 0 ? true : false} onClick={handleMerge}>
                            <Tooltip content="Merge">
                              <LuMerge />
                            </Tooltip>
                          </Button>
                          <Button
                            p={0}
                            borderRadius="full"
                            disabled={!canUseAlign}
                            onClick={() => {
                              if (!canUseAlign) return;
                              setGlobalMode((m) => (m === "align_endpoints" ? "none" : "align_endpoints"));
                            }}
                            bg={globalMode === "align_endpoints" ? "green" : "MenuText"}
                          >
                            <Tooltip content={"Align Endpoints " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.ALIGN_ENDPOINTS)}>
                              <LuSquareFunction color={"white"} />
                            </Tooltip>
                          </Button>

                          <Button colorPalette={"MenuText"} p={0} borderRadius="full" disabled={selectedEntities.length == 0 ? true : false} onClick={handleReverseWaypoints}>
                            <Tooltip content="Reverse Waypoints">
                              <LuSendToBack />
                            </Tooltip>
                          </Button>
                          <Button colorPalette={"MenuText"} p={0} borderRadius="full" disabled={selectedEntities.length == 0 ? true : false} onClick={handleDuplicate}>
                            <Tooltip content="Duplicate">
                              <LuCopy />
                            </Tooltip>
                          </Button>
                          <Button colorPalette={"MenuText"} p={0} borderRadius="full" disabled={selectedEntities.length == 0 ? true : false} onClick={handleEditTags}>
                            <Tooltip content="Edit Tags">
                              <LuSquarePen />
                            </Tooltip>
                          </Button>
                          <Button p={0} borderRadius="full" disabled={selectedEntities.length == 0} onClick={handleMoving} bg={isMovingMode ? "green" : "MenuText"}>
                            <Tooltip content="Moving">
                              <LuMove color={"white"} />
                            </Tooltip>
                          </Button>
                          <Button p={0} colorPalette={"MenuText"} borderRadius="full" disabled={selectedEntities.length == 0} onClick={handleRearrangePoints}>
                            <Tooltip content="Rearrange Points">
                              <LuEllipsis color={"white"} />
                            </Tooltip>
                          </Button>
                          <Button p={0} colorPalette={"MenuText"} borderRadius="full" disabled={!lastSelectedWithRectangle?.results?.p_p?.length ? true : false} onClick={handleAutoSnap}>
                            <Tooltip content={"Auto Snap " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.TOGGLE_AUTO_SNAP)}>
                              <LuGitMerge color={"white"} />
                            </Tooltip>
                          </Button>
                          <Button
                            p={0}
                            colorPalette={"MenuText"}
                            borderRadius="full"
                            disabled={
                              !(
                                (Array.isArray(lastSelectedWithRectangle?.results?.p_p)
                                  ? lastSelectedWithRectangle.results.p_p.length
                                  : lastSelectedWithRectangle?.results?.p_p
                                    ? Object.keys(lastSelectedWithRectangle.results.p_p).length
                                    : 0) ||
                                (Array.isArray(lastSelectedWithRectangle?.results?.p_f)
                                  ? lastSelectedWithRectangle.results.p_f.length
                                  : lastSelectedWithRectangle?.results?.p_f
                                    ? Object.keys(lastSelectedWithRectangle.results.p_f).length
                                    : 0)
                              )
                            }
                            onClick={handleChangeHeight}
                          >
                            <Tooltip content={"Change Height"}>
                              <LuSeparatorHorizontal color={"white"} />
                            </Tooltip>
                          </Button>
                          <Button
                            p={0}
                            colorPalette={"MenuText"}
                            borderRadius="full"
                            disabled={
                              !(selectionType === "p_p") ||
                              !(Array.isArray(lastSelectedWithRectangle?.results?.p_p)
                                ? lastSelectedWithRectangle.results.p_p.length >= 2
                                : lastSelectedWithRectangle?.results?.p_p
                                  ? Object.keys(lastSelectedWithRectangle.results.p_p).length >= 2
                                  : false)
                            }
                            onClick={handleAutoSnapCluster}
                          >
                            <Tooltip content={"Auto Snap (Cluster) " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.AUTO_SNAP_CLUSTER)}>
                              <LuMerge color={"white"} />
                            </Tooltip>
                          </Button>
                          <Button p={0} colorPalette={"MenuText"} disabled={!!!(globalUndoStackRef?.current && globalUndoStackRef.current.length > 0)} borderRadius="full" onClick={handleUndo}>
                            <Tooltip content="Undo">
                              <LuUndo2 color={"white"} />
                            </Tooltip>
                          </Button>
                        </SimpleGrid>
                      </VStack>
                      <Separator my={1} />
                    </>
                  ) : null}

                  {userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.DIGITIZER) || userType.includes(Type.ROLE.SUPER_ADMIN) ? (
                    <>
                      <VStack spacing={0} width={"full"}>
                        <Text width={"full"} fontWeight={"medium"} fontSize={"md"} mt={1}>
                          Lanelet Tools
                        </Text>
                        <SimpleGrid columns={9} gap={2} my={2} mt={0} width={"full"}>
                          <Button p={0} colorPalette={"MenuText"} disabled={isDisableSwitchLaneletSides} borderRadius="full" onClick={handleSwitchLaneletSides}>
                            <Tooltip content={"Switch Lanelet Sides " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.SWITCH_LANELET_SIDES)}>
                              <LuArrowRightLeft color={"white"} />
                            </Tooltip>
                          </Button>
                          <Button
                            p={0}
                            colorPalette={isEnableSwitchLaneletTurnDirection ? "green" : "MenuText"}
                            disabled={isDisableSwitchLaneletSides}
                            borderRadius="full"
                            onClick={handleSwitchLaneletTurnDirection}
                          >
                            <Tooltip content={"Switch Lanelet Turn Direction " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.SWITCH_LANELET_TURN_DIRECTION)}>
                              <LuUndo color={"white"} />
                            </Tooltip>
                          </Button>
                        </SimpleGrid>
                      </VStack>
                      <Separator my={1} />
                    </>
                  ) : null}

                  {userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.DIGITIZER) || userType.includes(Type.ROLE.SUPER_ADMIN) ? (
                    <>
                      <VStack spacing={0}>
                        <Text width={"full"} fontWeight={"medium"} fontSize={"md"} mt={1}>
                          Splitting Tools
                        </Text>
                        <HStack justify={"left"} justifyContent={"left"} width={"full"}>
                          <Button
                            px={0}
                            height={"25px"}
                            justifyContent={"left"}
                            onClick={() => {
                              if (globalMode == "split") {
                                setGlobalMode("none");
                                splitSingleUseRef.current = false;
                              } else {
                                setGlobalMode("split");
                                splitSingleUseRef.current = false;
                              }
                            }}
                            pr={1}
                            bg={globalMode == "split" ? "green" : "transparent"}
                            color={globalMode == "split" ? "white" : "MenuText"}
                            py={0}
                            my={0}
                          >
                            <LuSquareSplitHorizontal /> Split {getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.SPLIT)}
                          </Button>
                          <Button
                            px={0}
                            height={"25px"}
                            justifyContent={"left"}
                            onClick={() => {
                              undoLastCut(viewerRef.current, cutHistory, mapObjects, setMapObjects);
                            }}
                            pr={1}
                            bg={"transparent"}
                            color={"MenuText"}
                            py={0}
                            my={0}
                          >
                            <LuUndo2 /> Undo {getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.UNDO)}
                          </Button>
                          <Button
                            px={0}
                            height={"25px"}
                            justifyContent={"left"}
                            onClick={() => {
                              if (globalMode == "aplit_along_cutline") {
                                setGlobalMode("none");
                              } else {
                                setGlobalMode("aplit_along_cutline");
                                showToast("Cutline selection feature is active", Type.NOTIFICATION.INFO);
                                enableRectSelectAndCutByCutline(viewerRef.current, originalCameraControls, mapObjectsRef, setMapObjects, true, userData);
                              }
                            }}
                            pr={1}
                            bg={globalMode == "aplit_along_cutline" ? "green" : "transparent"}
                            color={globalMode == "aplit_along_cutline" ? "white" : "MenuText"}
                            py={0}
                            my={0}
                          >
                            <LuScissorsLineDashed /> Split Along Cutline
                          </Button>
                        </HStack>
                      </VStack>
                      <Separator my={1} />
                    </>
                  ) : null}

                  <VStack spacing={0}>
                    <Text width={"full"} fontWeight={"medium"} fontSize={"md"} mt={1}>
                      Topology Check
                    </Text>
                    <HStack justify={"left"} justifyContent={"left"} width={"full"}>
                      <Button
                        px={0}
                        height={"25px"}
                        justifyContent={"left"}
                        onClick={() => {
                          setIntersectionPointsEnabled((v) => !v);
                        }}
                        pr={1}
                        bg={intersectionPointsEnabled ? "green" : "transparent"}
                        color={intersectionPointsEnabled ? "white" : "MenuText"}
                        py={0}
                        my={0}
                      >
                        <LuCircleSlash /> Intersection Points {getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.TOPOLOGY_INTERSECTION_POINTS)}
                      </Button>
                    </HStack>
                    <HStack justify={"left"} justifyContent={"left"} width={"full"}>
                      <Button
                        px={0}
                        height={"25px"}
                        justifyContent={"left"}
                        onClick={() => {
                          if (globalMode == "dangling_lines") {
                            setGlobalMode("none");
                          } else {
                            setGlobalMode("dangling_lines");
                          }
                        }}
                        pr={1}
                        bg={globalMode == "dangling_lines" ? "green" : "transparent"}
                        color={globalMode == "dangling_lines" ? "white" : "MenuText"}
                        py={0}
                        my={0}
                      >
                        <LuCircleSlash /> Dangling Lines {getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.TOPOLOGY_DANGLING_LINES)}
                      </Button>
                    </HStack>
                  </VStack>
                </SimpleGrid>
              </Popover.Body>
            </Popover.Content>
          </Popover.Positioner>
        </Portal>
      </Popover.Root>
      {confirmState && (
        <Box position="fixed" bottom="24px" left="50%" transform="translateX(-50%)" bg="white" color="black" px={4} py={3} borderRadius="md" boxShadow="lg" zIndex={2000} minW="260px">
          <Text mb={2} fontSize="sm">
            Use this entity as next in chain?
          </Text>
          <HStack justify="flex-end" spacing={3}>
            <Button size="sm" variant="outline" onClick={handleConfirmNo}>
              No
            </Button>
            <Button size="sm" colorScheme="blue" onClick={handleConfirmYes}>
              Yes
            </Button>
          </HStack>
        </Box>
      )}
    </>
  );
}

export default GlobalTools;
